// mission-secrets-test-env-doctor-checks.ts — phase 04a (spec V2 §4.2 "Doctor",
// nghiệm thu (5)/(9)).
//
// Doctor: quyền/owner sai/file không an toàn ⇒ FAIL; key `ANTHROPIC_*` ⇒ FAIL
// (cùng luật `assertNoAnthropicEnv` của worker env; preflight `run` cũng chặn —
// mission-secrets-test-env-run-preflight.ts); key thiếu so `env.example`/
// `env_allowlist` (và không có ở shell) ⇒ cảnh báo theo TÊN; giá trị < 8 ký tự
// (đo sau khi bỏ một cặp nháy bao ngoài — review L9) ⇒ cảnh báo "không được che
// theo giá trị" theo TÊN; dòng sai dạng / key bị từ chối ⇒ cảnh báo (L3);
// account chạy ≠ `actor_binding.os_account` ⇒ cảnh báo (M4: owner kiểm theo uid
// của account chạy `run`); giá trị trùng giá trị shell cùng key ⇒ cảnh báo
// (review 04b M1: assert I3 không chặn trùng shell). Không đường nào ở đây in
// giá trị.
import { userInfo } from "node:os";
import type { DoctorCheck } from "./autopilot-environment-checks.js";
import { assertNoAnthropicEnv } from "./child-env-allowlist-builder.js";
import { readEnvKeyNamesLiveAndHead } from "./decision-auto-env-key-sources.js";
import { KNOWN_SECRET_VALUE_MIN_LENGTH } from "./known-secret-value-encoded-variants.js";
import {
  loadMissionTestEnv,
  type MissionTestEnvLoadResult,
} from "./mission-secrets-test-env-loader.js";
import {
  keysWithValuesTooShortToMask,
  testEnvValueCore,
} from "./mission-secrets-test-env-parser-and-permissions.js";

export interface MissionSecretsDoctorInput {
  missionId: string;
  missionWorkspace: string;
  /** Shell cha — key có ở đây thì không tính là "thiếu" (recipe nhận qua process.env). */
  env?: NodeJS.ProcessEnv;
  /** `actor_binding.os_account` của mission (vắng ⇒ bỏ check account). */
  boundOsAccount?: string;
  /** Account đang chạy — mặc định `os.userInfo().username`. */
  currentUsername?: string;
}

function anthropicCheck(keys: string[]): DoctorCheck {
  try {
    assertNoAnthropicEnv(Object.fromEntries(keys.map((k) => [k, ""])));
    return { name: "secrets:anthropic_keys", status: "ok", detail: "không có key ANTHROPIC_*" };
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    return { name: "secrets:anthropic_keys", status: "fail", detail };
  }
}

async function missingKeysCheck(
  input: MissionSecretsDoctorInput,
  fileKeys: string[],
): Promise<DoctorCheck> {
  const env = input.env ?? process.env;
  let expected: string[] = [];
  try {
    expected = await readEnvKeyNamesLiveAndHead(input.missionWorkspace);
  } catch {
    // worktree không đọc được — các check khác của doctor đã báo
  }
  const missing = expected.filter((k) => !fileKeys.includes(k) && env[k] === undefined);
  return missing.length === 0
    ? { name: "secrets:missing_keys", status: "ok", detail: "đủ key so env.example/env_allowlist" }
    : {
        name: "secrets:missing_keys",
        status: "warn",
        detail: `key thiếu (không có trong test.env lẫn shell): ${missing.join(", ")}`,
      };
}

function shortValuesCheck(values: Record<string, string>): DoctorCheck {
  const short = keysWithValuesTooShortToMask(values);
  return short.length === 0
    ? { name: "secrets:short_values", status: "ok", detail: "mọi giá trị ≥ 8 ký tự" }
    : {
        name: "secrets:short_values",
        status: "warn",
        detail: `giá trị < ${KNOWN_SECRET_VALUE_MIN_LENGTH} ký tự KHÔNG được che theo giá trị: ${short.join(", ")}`,
      };
}

/**
 * Vòng sửa 3 (review L6): value toàn chữ số (≥ 8, tức được đăng ký) có thể
 * trùng một field số của stream-json (`"input_tokens":12345678901`) ⇒ bị che ở
 * vị trí số ⇒ dòng JSON vỡ ⇒ result mất. Chỉ cảnh báo theo tên key.
 */
function numericValuesCheck(values: Record<string, string>): DoctorCheck {
  const numeric = Object.entries(values)
    .filter(([, v]) => {
      const value = testEnvValueCore(v);
      return value.length >= KNOWN_SECRET_VALUE_MIN_LENGTH && /^\d+$/.test(value);
    })
    .map(([k]) => k);
  return numeric.length === 0
    ? { name: "secrets:numeric_values", status: "ok", detail: "không có giá trị toàn chữ số" }
    : {
        name: "secrets:numeric_values",
        status: "warn",
        detail: `giá trị toàn chữ số có thể trùng số trong output worker (JSON vỡ khi che): ${numeric.join(", ")}`,
      };
}

/**
 * Review 04b M1: giá trị `test.env` trùng giá trị shell cùng key ⇒ nếu key đó
 * được builder chép từ shell sang worker (vd proxy/CA), assert I3 không chặn —
 * giá trị đến từ shell, không phải do code cấp. Chỉ tên key.
 */
function shellDuplicateValuesCheck(
  values: Record<string, string>,
  env: NodeJS.ProcessEnv,
): DoctorCheck {
  const same = Object.entries(values)
    .filter(([k, v]) => env[k] !== undefined && (env[k] === v || env[k] === testEnvValueCore(v)))
    .map(([k]) => k);
  return same.length === 0
    ? {
        name: "secrets:shell_duplicate_values",
        status: "ok",
        detail: "không key nào trùng giá trị shell",
      }
    : {
        name: "secrets:shell_duplicate_values",
        status: "warn",
        detail: `key test.env trùng giá trị shell (worker có thể thấy qua shell; I3 không chặn): ${same.join(", ")}`,
      };
}

function ownerAccountCheck(input: MissionSecretsDoctorInput): DoctorCheck | undefined {
  if (input.boundOsAccount === undefined) return undefined;
  let current = input.currentUsername;
  try {
    current ??= userInfo().username;
  } catch {
    // không xác định được account (môi trường lạ) — vẫn báo bên dưới
  }
  return current === input.boundOsAccount
    ? { name: "secrets:owner_account", status: "ok", detail: `owner = ${current}` }
    : {
        name: "secrets:owner_account",
        status: "warn",
        detail:
          `owner test.env được kiểm theo account đang chạy (${current ?? "?"}), khác account ` +
          `bound lúc init (${input.boundOsAccount})`,
      };
}

function fileCheck(result: MissionTestEnvLoadResult): DoctorCheck {
  const name = "secrets:test.env";
  switch (result.status) {
    case "absent":
      return { name, status: "ok", detail: "không có test.env (mission không dùng secret thử)" };
    case "unreadable":
      return { name, status: "fail", detail: `không đọc được ${result.path} (${result.detail})` };
    case "invalid_permissions":
      return { name, status: "fail", detail: result.problems.join("; ") };
    case "ok": {
      const notes = [
        ...(result.malformedLines.length > 0
          ? [`dòng sai dạng KEY=VALUE không nạp: ${result.malformedLines.join(", ")}`]
          : []),
        ...(result.rejectedKeys.length > 0
          ? [`key bị từ chối không nạp: ${result.rejectedKeys.join(", ")}`]
          : []),
      ];
      const base = `${result.path}: ${Object.keys(result.values).length} key`;
      return notes.length === 0
        ? { name, status: "ok", detail: base }
        : { name, status: "warn", detail: `${base} — ${notes.join("; ")}` };
    }
  }
}

export async function missionSecretsDoctorChecks(
  input: MissionSecretsDoctorInput,
): Promise<DoctorCheck[]> {
  const result = await loadMissionTestEnv(input.missionId);
  const checks = [fileCheck(result)];
  if (result.status === "absent") return checks;
  const owner = ownerAccountCheck(input);
  if (owner !== undefined) checks.push(owner);
  const keys =
    result.status === "ok"
      ? Object.keys(result.values)
      : result.status === "invalid_permissions"
        ? result.keys
        : [];
  if (keys.length === 0) return checks;
  checks.push(anthropicCheck(keys), await missingKeysCheck(input, keys));
  if (result.status === "ok") {
    checks.push(
      shortValuesCheck(result.values),
      numericValuesCheck(result.values),
      shellDuplicateValuesCheck(result.values, input.env ?? process.env),
    );
  }
  return checks;
}
